import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isEqual } from 'lodash';
import { toast } from '../../Common/ToastNotification';

//Legacy code
import listService from "ListService";
import sequenceService from "SequenceService";

//Helpers
import { processControlModel, generateAttributes, createDefaultSectionObject, generateSectionProperties, syncGlobalStylesToThemeService,
    syncMarginsWithGlobalSpacing, initializeControlForLayout } from '../utils/mobileUtils';



export const useWizardData = ({ initialData, parentSetIsDirty }) => {

    const [pages, setPages] = useState(initialData.pages);
    const [appName, setAppName] = useState(initialData.appName);
    const [appAttributes, setAppAttributes] = useState(initialData.appAttributes);
    const [activePageId, setActivePageId] = useState(initialData.pages[0]?.id || null);
    const [layout, setLayout] = useState(initialData.layout);
    const [globalStyles, _setGlobalStyles] = useState(initialData.globalStyles);

    const [availableControls, setAvailableControls] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    
    // This ref holds the snapshot for dirty checking
    const initialState = useRef({
        layout: JSON.parse(JSON.stringify(initialData.layout)),
        globalStyles: JSON.parse(JSON.stringify(initialData.globalStyles)),
        initialObjectsSnapshot: initialData.initialObjectsSnapshot || {}
    });
   
    useEffect(() => {
        if (!initialState.current) return;

        const isLayoutDirty = !isEqual(layout, initialState.current.layout);
        const isStylesDirty = !isEqual(globalStyles, initialState.current.globalStyles);

        const newDirtyState = isLayoutDirty || isStylesDirty;

        setIsDirty(newDirtyState);
        if (parentSetIsDirty) {
            parentSetIsDirty(newDirtyState);
        }

    }, [layout, globalStyles, parentSetIsDirty]);

    useEffect(() => {
        if (!activePageId) return;

        const getAllPlacedIds = (controls) => {
            return controls.flatMap(c => {
                // Return this control's ID + any children IDs recursively
                const childIds = (c.children && c.children.length > 0) 
                    ? getAllPlacedIds(c.children) 
                    : [];
                return [c.id, ...childIds];
            });
        };

        const placedControlIds = new Set(
            (layout[activePageId] || []).flatMap(section => getAllPlacedIds(section.controls))
        );
        const allDesktopControlsRaw = listService.getPageControls(activePageId);

        const allControlsProcessed = allDesktopControlsRaw.map(control => {
            const processed = processControlModel(control.model);
            processed.isPlaced = placedControlIds.has(processed.id);
            return processed;
        }).toArray();

        setAvailableControls(prev => ({
            ...prev,
            [activePageId]: allControlsProcessed
        }));

    }, [layout, activePageId]);

    const setGlobalStyles = (updater) => {
        const newStyles = typeof updater === 'function' ? updater(globalStyles) : updater;

        const marginValue = parseInt(newStyles.marginSpacing, 10);
        const widthValue = parseInt(newStyles.maxAppWidth, 10);
        if (!isNaN(marginValue)) {
            newStyles.marginSpacing = Math.max(0, marginValue);
        }
        if (!isNaN(widthValue)) {
            newStyles.maxAppWidth = Math.max(450, widthValue);
        }

        _setGlobalStyles(newStyles);
        syncGlobalStylesToThemeService(newStyles);

    };

    const currentDefaultMargins = useMemo(() => {
        const spacing = globalStyles?.marginSpacing || 8;
        const type = globalStyles?.defaultMargin || 'bottom';
        return syncMarginsWithGlobalSpacing(type, spacing);
    }, [globalStyles]);

    const renderableLayout = useMemo(() => {
        if (!globalStyles) return {};

        const newRenderableLayout = {};
        const initialTheme = initialState.current.globalStyles;

        const processControlRecursive = (control) => {
            const globalSpacingSize = globalStyles.marginSpacing || 8;
            const globaldefaultMargin = globalStyles.defaultMargin || 'bottom';
            
            const recalculatedMargins = syncMarginsWithGlobalSpacing(control.margins, globaldefaultMargin, globalSpacingSize);
    
            const finalAttributes = generateAttributes(
                control, 
                globalStyles, 
                initialTheme
            );
            
            finalAttributes.margins = recalculatedMargins;
    
            let processedChildren = control.children;
            if (Array.isArray(control.children) && control.children.length > 0) {
                processedChildren = control.children.map(child => processControlRecursive(child));
            }
    
            return { 
                ...control, 
                margins: recalculatedMargins, 
                attributes: finalAttributes,
                children: processedChildren
            };
        };

        Object.keys(layout).forEach(pageId => {
            newRenderableLayout[pageId] = (layout[pageId] || []).map(section => {

                let finalSectionProps = generateSectionProperties(section, null, globalStyles, initialTheme);

                const renderableControls = section.controls.map(control => processControlRecursive(control));

                return { ...section, attributes: finalSectionProps, controls: renderableControls };
            });
        });
        return newRenderableLayout;
    }, [layout, globalStyles]);

    const addSection = ({ pageId }) => {
        const newSectionId = sequenceService.nextDomId();
        const newSectionName = 'New Section';
        const pageSections = layout[pageId] || [];
        const defaultColumn = listService.getColumn(pageId, 0);

        const newSectionObject = createDefaultSectionObject(
            newSectionId,
            newSectionName,
            pageId,
            defaultColumn ? defaultColumn.id : null,
            pageSections.length + 1
        );

        setLayout(prevLayout => {
            const newLayout = { ...prevLayout };
            const currentPageLayout = newLayout[pageId] || [];
            newLayout[pageId] = [...currentPageLayout, newSectionObject];
            return newLayout;
        });
    };

    const renameSection = ({ pageId, sectionId, newName }) => {
        setLayout(prevLayout => {
            const newLayout = JSON.parse(JSON.stringify(prevLayout));
            if (!newLayout[pageId]) return prevLayout; // No change

            const pageSections = newLayout[pageId];
            const sectionIndex = pageSections.findIndex(s => s.id === sectionId);
            if (sectionIndex > -1) {
                pageSections[sectionIndex].name = newName;
                if (!pageSections[sectionIndex].attributes) {
                    pageSections[sectionIndex].attributes = {};
                }
                pageSections[sectionIndex].attributes.titleText = newName;
            }
            return newLayout;
        });
    };

    const moveControl = useCallback(({ pageId, controlId, fromSectionId, fromIndex, toSectionId, toIndex }) => {
        setLayout(currentLayout => {
            const newLayout = JSON.parse(JSON.stringify(currentLayout));
            if (!newLayout[pageId]) return currentLayout;

            const pageSections = newLayout[pageId];
            let controlToMove;

            if (!fromSectionId) {
                const desktopControl = listService.getObject(controlId);
                if (!desktopControl) return currentLayout;
                const rawControl = processControlModel(desktopControl.model);
                controlToMove = initializeControlForLayout(rawControl, currentDefaultMargins);
            } else {
                const fromSection = pageSections.find(s => s.id === fromSectionId);
                if (!fromSection) return currentLayout;
                [controlToMove] = fromSection.controls.splice(fromIndex, 1);
            }

            if (!controlToMove) return currentLayout;

            const toSection = pageSections.find(s => s.id === toSectionId);
            if (!toSection) return currentLayout;

            toSection.controls.splice(toIndex, 0, controlToMove);

            return newLayout;
        });

    }, [currentDefaultMargins]);

    const removeControl = ({ pageId, controlId }) => {
        setLayout(prevLayout => {
            const newLayout = JSON.parse(JSON.stringify(prevLayout));
            if (!newLayout[pageId]) return prevLayout;

            const pageSections = newLayout[pageId];
            pageSections.forEach((section) => {
                const controlIndex = section.controls.findIndex(c => c.id === controlId);
                if (controlIndex > -1) {
                    section.controls.splice(controlIndex, 1);
                }
            });

            return newLayout;
        });
    };

    const findControlLocation = (pageId, controlId) => {
        if (!layout[pageId]) return null;
        for (const section of layout[pageId]) {
            const controlIndex = section.controls.findIndex(c => c.id === controlId);
            if (controlIndex > -1) {
                return { section, control: section.controls[controlIndex], controlIndex };
            }
        }
        return null;
    };

    const moveControlUp = ({ pageId, controlId }) => {
        const location = findControlLocation(pageId, controlId);
        if (!location || location.controlIndex === 0) return;

        moveControl({
            pageId,
            controlId,
            fromSectionId: location.section.id,
            fromIndex: location.controlIndex,
            toSectionId: location.section.id,
            toIndex: location.controlIndex - 1,
        });
    };

    const moveControlDown = ({ pageId, controlId }) => {
        const location = findControlLocation(pageId, controlId);
        if (!location || location.controlIndex >= location.section.controls.length - 1) return;

        moveControl({
            pageId,
            controlId,
            fromSectionId: location.section.id,
            fromIndex: location.controlIndex,
            toSectionId: location.section.id,
            toIndex: location.controlIndex + 1,
        });
    };

    const addAllControls = useCallback(({ pageId, sectionId }) => {
        const placedControlIds = new Set(
            (layout[pageId] || []).flatMap(section => section.controls.map(c => c.id))
        );

        const rawControls = listService.getPageControls(activePageId);
        const allDesktopControlsRaw = rawControls.filter(control => !control['_groupId']);
        
        const controlsToAdd = allDesktopControlsRaw
            .map(control => {
                if (control.model.get('groupId')) return null; //skip children
                const processed = processControlModel(control.model);
                // Add only if it's draggable and not already placed
                if (processed.isDraggable && !placedControlIds.has(processed.id)) {
                    return initializeControlForLayout(processed, currentDefaultMargins);
                }
                return null;
            })
            .filter(Boolean)
            .sort((a, b) => a.tabIndex - b.tabIndex)
            .valueSeq()
            .toArray();;

        if (controlsToAdd.length === 0) {
            toast.show({ type: 'info', message: 'No available controls to add on this page.' });
            console.log('No available controls to add.');
            return;
        }

        setLayout(currentLayout => {
            const newLayout = JSON.parse(JSON.stringify(currentLayout));
            if (!newLayout[pageId]) return currentLayout;

            const pageSections = newLayout[pageId];
            const sectionIndex = pageSections.findIndex(s => s.id === sectionId);

            if (sectionIndex > -1) {
                pageSections[sectionIndex].controls.push(...controlsToAdd);
            }

            return newLayout;
        });

    }, [layout, activePageId]);

    const moveSection = (pageId, dragIndex, hoverIndex) => {
        setLayout(prevLayout => {
            if (!prevLayout[pageId]) return prevLayout;

            const newLayout = JSON.parse(JSON.stringify(prevLayout));
            const reorderedSections = newLayout[pageId];

            const [draggedSection] = reorderedSections.splice(dragIndex, 1);
            reorderedSections.splice(hoverIndex, 0, draggedSection);

            return newLayout;
        });
    }

    const deleteSection = ({ pageId, sectionId }) => {
        setLayout(prev => {
            const newLayout = JSON.parse(JSON.stringify(prev));
            if (!newLayout[pageId]) return prev;

            newLayout[pageId] = newLayout[pageId].filter(s => s.id !== sectionId);
            return newLayout;
        });
    };

    const moveSectionUp = ({ pageId, sectionId }) => {
        const pageLayout = layout[pageId];
        if (!pageLayout) return;

        const index = pageLayout.findIndex(s => s.id === sectionId);
        if (index > 0) {
            moveSection(pageId, index, index - 1);
        }
    };

    const moveSectionDown = ({ pageId, sectionId }) => {
        const pageLayout = layout[pageId];
        if (!pageLayout) return;

        const index = pageLayout.findIndex(s => s.id === sectionId);
        if (index < pageLayout.length - 1) {
            moveSection(pageId, index, index + 1);
        }
    };

    const removeAllControls = useCallback(({ pageId, sectionId = null }) => {
        setLayout(currentLayout => {
            const newLayout = JSON.parse(JSON.stringify(currentLayout));
            if (!newLayout[pageId]) return currentLayout;

            if (sectionId) {
                const section = newLayout[pageId].find(s => s.id === sectionId);
                if (section) {
                    section.controls = [];
                }
            } else {
                newLayout[pageId].forEach(section => {
                    section.controls = [];
                });
            }
            return newLayout;
        });
    }, []);

    return {
        pages,
        appName,
        appAttributes,
        activePageId,
        setActivePageId,
        layout: renderableLayout,
        rawLayout: layout,
        moveSection,
        moveControl,
        removeControl,
        moveControlUp,
        moveControlDown,
        addSection,
        addAllControls,
        removeAllControls,
        globalStyles,
        setGlobalStyles,
        renameSection,
        deleteSection,
        moveSectionUp,
        moveSectionDown,
        isDirty,
        setIsDirty,
        availableControls,
        initialState
    };
};